import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { AuthType, ConnectionFormValues, Transport } from '../types';

const TRANSPORTS: Transport[] = ['stdio', 'sse', 'streamable-http'];
const AUTH_TYPES: AuthType[] = ['none', 'bearer', 'apiKey', 'header'];

type Props = {
  open: boolean;
  title: string;
  initial: ConnectionFormValues;
  onClose: () => void;
  onSave: (values: ConnectionFormValues) => void;
  onSaveAndTest?: (values: ConnectionFormValues) => void;
};

export default function ConnectionForm({
  open,
  title,
  initial,
  onClose,
  onSave,
  onSaveAndTest,
}: Props) {
  const [values, setValues] = useState<ConnectionFormValues>(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValues(initial);
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const set = <K extends keyof ConnectionFormValues>(key: K, value: ConnectionFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): boolean => {
    if (!values.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (values.transport === 'stdio' && !values.command.trim()) {
      setError('Command is required for stdio transport');
      return false;
    }
    if (values.transport !== 'stdio' && !values.url.trim()) {
      setError('URL is required for remote transports');
      return false;
    }
    if (values.authType !== 'none' && !values.authValue.trim()) {
      setError(`Auth value is required for ${values.authType}`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(values);
  };

  const handleSaveAndTest = () => {
    if (!validate() || !onSaveAndTest) return;
    onSaveAndTest(values);
  };

  const isStdio = values.transport === 'stdio';

  return createPortal(
    <div className="mcp-v2__overlay" role="presentation" onClick={onClose}>
      <div
        className="mcp-v2__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-v2-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mcp-v2__modal-header">
          <h2 id="mcp-v2-form-title">{title}</h2>
          <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mcp-v2__modal-body">
          {error ? <div className="mcp-v2__alert mcp-v2__alert--error">{error}</div> : null}

          <div className="mcp-v2__field">
            <label htmlFor="mcp-v2-name">Name *</label>
            <input
              id="mcp-v2-name"
              className="mcp-v2__input"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="My MCP server"
            />
          </div>

          <div className="mcp-v2__field">
            <label htmlFor="mcp-v2-desc">Description</label>
            <textarea
              id="mcp-v2-desc"
              className="mcp-v2__textarea"
              rows={2}
              value={values.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="What this connection is used for"
            />
          </div>

          <div className="mcp-v2__row">
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-transport">Transport *</label>
              <select
                id="mcp-v2-transport"
                className="mcp-v2__select"
                style={{ width: '100%' }}
                value={values.transport}
                onChange={(e) => set('transport', e.target.value as Transport)}
              >
                {TRANSPORTS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-auth">Auth</label>
              <select
                id="mcp-v2-auth"
                className="mcp-v2__select"
                style={{ width: '100%' }}
                value={values.authType}
                onChange={(e) => set('authType', e.target.value as AuthType)}
              >
                {AUTH_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isStdio ? (
            <>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-cmd">Command *</label>
                <input
                  id="mcp-v2-cmd"
                  className="mcp-v2__input"
                  value={values.command}
                  onChange={(e) => set('command', e.target.value)}
                  placeholder="npx"
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-args">Args (one per line or comma-separated)</label>
                <textarea
                  id="mcp-v2-args"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.argsText}
                  onChange={(e) => set('argsText', e.target.value)}
                  placeholder={'-y\n@modelcontextprotocol/server-filesystem\n/workspace'}
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-env">Environment (JSON or KEY=value lines)</label>
                <textarea
                  id="mcp-v2-env"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.envText}
                  onChange={(e) => set('envText', e.target.value)}
                  placeholder={'{\n  "API_KEY": ""\n}'}
                />
              </div>
            </>
          ) : (
            <>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-url">URL *</label>
                <input
                  id="mcp-v2-url"
                  className="mcp-v2__input"
                  value={values.url}
                  onChange={(e) => set('url', e.target.value)}
                  placeholder="https://mcp.example.com/sse"
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-headers">Headers (JSON or KEY=value lines)</label>
                <textarea
                  id="mcp-v2-headers"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.headersText}
                  onChange={(e) => set('headersText', e.target.value)}
                  placeholder={'{\n  "X-Tenant": "demo"\n}'}
                />
              </div>
            </>
          )}

          {values.authType !== 'none' ? (
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-auth-val">Auth value *</label>
              <input
                id="mcp-v2-auth-val"
                className="mcp-v2__input"
                type="password"
                autoComplete="off"
                value={values.authValue}
                onChange={(e) => set('authValue', e.target.value)}
                placeholder={
                  values.authType === 'bearer'
                    ? 'Bearer token'
                    : values.authType === 'apiKey'
                      ? 'API key'
                      : 'Header value'
                }
              />
            </div>
          ) : null}

          <div className="mcp-v2__field">
            <label htmlFor="mcp-v2-tags">Tags (comma-separated)</label>
            <input
              id="mcp-v2-tags"
              className="mcp-v2__input"
              value={values.tagsText}
              onChange={(e) => set('tagsText', e.target.value)}
              placeholder="devtools, remote"
            />
          </div>

          <label className="mcp-v2__check">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            Active
          </label>
        </div>
        <div className="mcp-v2__modal-footer">
          <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={onClose}>
            Cancel
          </button>
          {onSaveAndTest ? (
            <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={handleSaveAndTest}>
              Save &amp; test
            </button>
          ) : null}
          <button type="button" className="mcp-v2__btn mcp-v2__btn--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
